import { LoadingScreen } from '@cabonline/ui-web';
import { ApiError, PermissionKey, useAuth } from '@multiplatform-apps/service-trips';
import { CookieConsent } from '../components/cookie-consent';
import { useInitApp } from '../hooks/use-init-app';
import { AuthCallback } from '../pages/auth/callback';
import React, { PropsWithChildren, Suspense, useEffect } from 'react';
import {
  Navigate,
  Outlet,
  ScrollRestoration,
  createBrowserRouter,
  useLocation,
  useSearchParams,
} from 'react-router-dom';

import { Layout } from '../components/layout';
import { paths } from '../constants';
import { ENABLE_ABSENCE_LIST } from '../constants/feature-toggles';
import { LayoutProvider, NavigationHistoryProvider } from '../context';
import { usePermissions } from '../hooks';
import { Error } from '../pages/error';
import { LandingPage } from '../pages/landing-page';

// ─── Lazy-loaded page chunks ────────────────────────────────────────────────
const About = React.lazy(() => import('../pages/about/about').then(m => ({ default: m.About })));
const Manuals = React.lazy(() =>
  import('../pages/manuals/manuals').then((m) => ({ default: m.Manuals })),
);
const Profile = React.lazy(() => import('../pages/profile/profile').then(m => ({ default: m.Profile })));
const AgreementPartners = React.lazy(() => import('../pages/agreement-partners/agreement-partners').then(m => ({ default: m.AgreementPartners })));
const GeneralAbsence = React.lazy(() => import('../pages/absence/absence').then(m => ({ default: m.GeneralAbsence })));
const AbsenceList = React.lazy(() => import('../pages/absence-list/absence-list').then(m => ({ default: m.AbsenceList })));
const Notifications = React.lazy(() => import('../pages/notifications/notifications').then(m => ({ default: m.Notifications })));
const Institutions = React.lazy(() => import('../pages/institutions/institutions').then(m => ({ default: m.Institutions })));
const TripsRoutes = React.lazy(() => import('../pages/trips/trips-routes').then(m => ({ default: m.TripsRoutes })));
const UsersRoutes = React.lazy(() => import('../pages/users/users-routes').then(m => ({ default: m.UsersRoutes })));
const PassengersRoutes = React.lazy(() => import('../pages/passengers/passengers-routes').then(m => ({ default: m.PassengersRoutes })));

const LazyPage: React.FC<PropsWithChildren> = ({ children }) => (
  <Suspense fallback={<LoadingScreen />}>{children}</Suspense>
);

const NotFound = () => {
  throw new ApiError(404, 'Not Found');
};

const DevErrorTrigger = () => {
  const params = new URLSearchParams(window.location.search);
  const type = params.get('type');
  if (type === '403') throw new ApiError(403, 'Forbidden');
  if (type === 'network') throw new ApiError(0, 'Network Error');
  if (type === 'generic') throw new ApiError(500, 'Internal Server Error');
  throw new ApiError(404, 'Not Found');
};

const RouterLoader: React.FC<PropsWithChildren> = ({ children }) => {
  const appIsReady = useInitApp();
  const [params] = useSearchParams();
  const autostart = params.get('autostart');

  if (!appIsReady) {
    return (
      <LayoutProvider>
        <Layout>
          <LoadingScreen />
        </Layout>
      </LayoutProvider>
    );
  }
  return (
    <>
      {children}
      {autostart !== 'true' && <CookieConsent />}
    </>
  );
};

const RequireAuth = ({
  children,
  redirectTo = paths.landingPage,
  permissionKey,
}: {
  children: React.JSX.Element;
  redirectTo?: string;
  permissionKey?: PermissionKey;
}) => {
  const { isAuthenticated, identity, logout } = useAuth();
  const location = useLocation();
  const { hasRootRoutePermission: hasPermission } = usePermissions();

  // Authenticated but no identity is an invalid state — force logout with a session-expired
  // banner. Done in an effect, not render: logout() clears query cache/context state, and
  // calling it directly in the render body is a side effect React (StrictMode) double-invokes.
  useEffect(() => {
    if (isAuthenticated && !identity) {
      localStorage.setItem('session_expired_at', Date.now().toString());
      logout();
    }
  }, [isAuthenticated, identity, logout]);

  if (!isAuthenticated) {
    return <Navigate to={redirectTo} state={{ from: location }} replace />;
  }
  if (!identity) {
    return null;
  }
  if (permissionKey && !hasPermission(permissionKey)) {
    throw new ApiError(403, 'Forbidden');
  }
  return children;
};

const AppLayout: React.FC<{ children: React.ReactNode; includeScrollRestoration?: boolean }> = ({
  children,
  includeScrollRestoration = false,
}) => (
  <>
    <RouterLoader>
      <NavigationHistoryProvider>
        <LayoutProvider>
          <Layout>{children}</Layout>
        </LayoutProvider>
      </NavigationHistoryProvider>
    </RouterLoader>
    {includeScrollRestoration && <ScrollRestoration />}
  </>
);

const Root = () => (
  <AppLayout includeScrollRestoration>
    <Outlet />
  </AppLayout>
);

// Child routes of <Root/> already render inside its layout — adding another would duplicate the header.
const RouteErrorElement = () => <Error />;

// Top-level routes have no ancestor layout, so their error element must supply one.
const RootErrorElement = () => (
  <AppLayout>
    <Error />
  </AppLayout>
);

const HomePage = () => {
  const { isAuthenticated } = useAuth();

  if (!isAuthenticated) {
    return <Navigate to={paths.landingPage} replace />;
  }

  return <Navigate to={paths.trips.root} replace />;
};

export const router = createBrowserRouter([
  {
    path: paths.landingPage,
    errorElement: <RootErrorElement />,
    element: (
      <RouterLoader>
        <LandingPage />
      </RouterLoader>
    ),
  },
  {
    path: paths.root,
    element: <Root />,
    // Prevents React Router's default loading UI — app uses its own LoadingScreen
    loader: () => null,
    errorElement: <RootErrorElement />,
    children: [
      {
        path: paths.root,
        element: <HomePage />,
      },
      {
        path: paths.profile.root,
        errorElement: <RouteErrorElement />,
        element: (
          <RequireAuth permissionKey="profilePage">
            <LazyPage><Profile /></LazyPage>
          </RequireAuth>
        ),
      },
      {
        path: `${paths.trips.root}/*`,
        errorElement: <RouteErrorElement />,
        element: (
          <RequireAuth permissionKey="tripsPage">
            <LazyPage><TripsRoutes /></LazyPage>
          </RequireAuth>
        ),
      },
      {
        path: `${paths.users.root}/*`,
        errorElement: <RouteErrorElement />,
        element: (
          <RequireAuth permissionKey="usersPage">
            <LazyPage><UsersRoutes /></LazyPage>
          </RequireAuth>
        ),
      },
      {
        path: `${paths.passengers.root}/*`,
        errorElement: <RouteErrorElement />,
        element: (
          <RequireAuth permissionKey="passengersPage">
            <LazyPage><PassengersRoutes /></LazyPage>
          </RequireAuth>
        ),
      },
      {
        path: paths.about.root,
        errorElement: <RouteErrorElement />,
        element: <LazyPage><About /></LazyPage>,
      },
      {
        path: paths.manuals.root,
        errorElement: <RouteErrorElement />,
        element: (
          <LazyPage>
            <Manuals />
          </LazyPage>
        ),
      },
      {
        path: paths.agreementPartners.root,
        errorElement: <RouteErrorElement />,
        element: (
          <RequireAuth permissionKey="adminPage">
            <LazyPage><AgreementPartners /></LazyPage>
          </RequireAuth>
        ),
      },
      {
        path: paths.absence.root,
        errorElement: <RouteErrorElement />,
        element: (
          <RequireAuth permissionKey="absencePage">
            <LazyPage><GeneralAbsence /></LazyPage>
          </RequireAuth>
        ),
      },
      ...(ENABLE_ABSENCE_LIST ? [{
        path: paths.absence.list,
        errorElement: <RouteErrorElement />,
        element: (
          <RequireAuth permissionKey="absencePage">
            <LazyPage><AbsenceList /></LazyPage>
          </RequireAuth>
        ),
      }] : []),
      {
        path: paths.institutions.root,
        errorElement: <RouteErrorElement />,
        element: (
          <RequireAuth permissionKey="institutionsPage">
            <LazyPage><Institutions /></LazyPage>
          </RequireAuth>
        ),
      },
      {
        path: paths.notifications.root,
        errorElement: <RouteErrorElement />,
        element: (
          <RequireAuth permissionKey="notificationsPage">
            <LazyPage><Notifications /></LazyPage>
          </RequireAuth>
        ),
      },
      { path: paths.authCallback.callback, element: <AuthCallback /> },
      ...(process.env['NODE_ENV'] === 'development'
        ? [{ path: '__dev/error', element: <DevErrorTrigger /> }]
        : []),
      { path: '*', element: <NotFound /> },
    ],
  },
]);
